import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { InviteMembers } from '@/features/invites/components/invite-members';
import { ReloadAlert } from '@/features/payout-accounts/components/age-verification-required';
import { CreatePayoutAccountForm } from '@/features/payout-accounts/components/create-payout-account-form';
import { useCreatePayoutAccount } from '@/features/payout-accounts/api/create-payout-account';
import { useSelectBankAccount } from '@/features/payout-accounts/hooks/use-select-bank-account';
import { useAuth } from '@/hooks/auth/use-auth';
import { useQueryParams } from '@/hooks/shared/use-query-params';
import { paths } from '@/config/paths';
import { Button } from '@/shared/components/ui/button';
import { LoadingButton } from '@/shared/components/ui/buttons/loading-button';
import { FormFooterProps } from '@/types/form/form-props';
import { MembershipRole } from '@/types/api/membership';
import { PayoutMethod } from '@/types/api/payout-account';
import { getGroupQueryOptions } from '../../api/get-group';
import { useOnboardingPrefs } from '../../stores/onboarding-prefs.store';
import { onboardingMutationKey } from '../../hooks/use-onboarding-progress';
import { OnboardingTaskKey } from '@/types/api/group/onboarding-task';

const AGE_LIMIT = 18;

/**
 * The payout account step: BankID first (to know the leader's age), then the
 * same form as on the payout settings page. Under-18 leaders are pointed to
 * inviting an adult leader instead, which their answer at registration lets
 * us do before BankID.
 */
export function PayoutTask({ groupId }: { groupId: string }) {
  const { context } = useAuth();
  const prefs = useOnboardingPrefs(groupId);
  const bankid = useQueryParams().getQueryParamByKey('bankid');

  const age = context?.age;

  if (age !== undefined && age !== null) {
    return age < AGE_LIMIT ? <InviteAdultLeader groupId={groupId} /> : <PayoutAccountForm groupId={groupId} />;
  }

  if (prefs.isAdult === false) {
    return <InviteAdultLeader groupId={groupId} showBankIdFallback />;
  }

  return (
    <div className="space-y-4">
      {bankid === 'done' && <ReloadAlert />}
      <p className="text-sm">
        För att hantera gruppens pengar behöver du verifiera dig med BankID. Det är för er säkerhet och för att följa
        gällande regler.
      </p>
      <div className="flex flex-col md:flex-row gap-2">
        <Button asChild>
          <Link to={paths.auth.bankid.getHref(paths.group.onboarding.getHref())}>Verifiera med BankID</Link>
        </Button>
        <InviteMembers roles={[MembershipRole.Leader, MembershipRole.Member]} orgId={groupId} variant={'outline'}>
          Under 18? Bjud in myndig ledare
        </InviteMembers>
      </div>
    </div>
  );
}

function InviteAdultLeader({ groupId, showBankIdFallback }: { groupId: string; showBankIdFallback?: boolean }) {
  return (
    <div className="space-y-4">
      <p className="text-sm">
        Eftersom du är under 18 år behöver en myndig gruppledare registrera utbetalningskontot. Skicka en inbjudan till
        en förälder eller tränare – sen fortsätter hen härifrån.
      </p>
      <InviteMembers roles={[MembershipRole.Leader, MembershipRole.Member]} orgId={groupId}>
        Bjud in myndig gruppledare
      </InviteMembers>
      {showBankIdFallback && (
        <p className="text-xs text-muted-foreground">
          Har du fyllt 18?{' '}
          <Link className="underline" to={paths.auth.bankid.getHref(paths.group.onboarding.getHref())}>
            Verifiera med BankID
          </Link>
        </p>
      )}
    </div>
  );
}

function PayoutAccountForm({ groupId }: { groupId: string }) {
  const queryClient = useQueryClient();
  // Set when returning from the bank's account selection, which comes back to this page
  const { id: selectionId, errorCode, clear: clearBankSelection } = useSelectBankAccount();
  const mutation = useCreatePayoutAccount({
    mutationConfig: {
      mutationKey: onboardingMutationKey(groupId, OnboardingTaskKey.CreatePayoutAccount),
      onSuccess: () => {
        clearBankSelection();
        return queryClient.invalidateQueries({ queryKey: getGroupQueryOptions(groupId).queryKey });
      },
    },
  });

  return (
    <CreatePayoutAccountForm
      // Back from the bank's account selection: continue with bank transfer, otherwise start on Swish
      defaultValues={
        selectionId || errorCode
          ? { method: PayoutMethod.BankTransfer, selectionId: selectionId ?? '' }
          : { method: PayoutMethod.Swish, swishNumber: '' }
      }
      onSubmit={async (data) => await mutation.mutateAsync({ data })}
      Footer={PayoutFormFooter}
    />
  );
}

function PayoutFormFooter({ canSubmit, isSubmitting }: FormFooterProps) {
  return (
    <div className="flex justify-end">
      <LoadingButton disabled={!canSubmit} isLoading={isSubmitting} type="submit">
        Spara utbetalningskonto
      </LoadingButton>
    </div>
  );
}
